import * as THREE from 'three';
import { getCamera, getCameraWorldTransform, getRect } from '../../modules/player';
import { find, get, getWorldTransform, set } from '../../modules/scene';
import { getSelectedNodes } from '../../modules/selection';
import { drawRotateGizmo, highlightAxis, getViewScale } from './gizmos';
import getBoundingRadius from './utils/getBoundingRadius';
const { min, PI, sqrt } = Math;

const parentInvMatrix = new THREE.Matrix4();
const nodeRotationQuaternion = new THREE.Quaternion();
const nodePosition = new THREE.Vector3();
const nodeScale = new THREE.Vector3();
const nodeRotationEuler = new THREE.Euler();
const cameraPosition = new THREE.Vector3();

export default function nodeRotate(store, translator) {
  let activeAxis = false;

  return {
    label: 'Rotate Node',
    enabled: true,
    display: {
      playerTools: false,
    },
    options: {},

    tool: {
      hover: function (ev) {
        if (!this.active) return;
        activeAxis = highlightAxis(translator, ev, activeAxis);
      },

      drag: function (ev) {
        if (ev.which !== 1 || !this.active || ev.metaKey) return false;

        return {
          momentum: !activeAxis,
          handle: function (ev) {
            rotateNode(store, ev.deltaX, ev.deltaY, activeAxis);
          },
        };
      },
    },

    widget: {
      draw: function (manipulatorsObject) {
        manipulatorsObject.add(drawRotateGizmo());
        const scale = getViewScale(store);
        manipulatorsObject.scale.set(scale, scale, scale);
      },

      position: function (manipulatorsObject) {
        const selectedNodes = getSelectedNodes(store);
        if (!selectedNodes.length) {
          manipulatorsObject.visible = false;
          return;
        }

        manipulatorsObject.visible = true;
        const manipId = selectedNodes[0];
        const worldTransform = getWorldTransform(store, manipId);
        const position = new THREE.Vector3();
        position.setFromMatrixPosition(worldTransform);
        manipulatorsObject.position.set(position.x, position.y, position.z);

        const scale = getViewScale(store);
        manipulatorsObject.scale.set(scale, scale, scale);
      },
    },
  };
}

function rotateNode(store, x, y, restrictToAxis) {
  const rect = getRect(store);
  const selectedNodes = getSelectedNodes(store);
  if (!selectedNodes.length) return;
  const manipId = selectedNodes[0];
  const nodeTransform = getWorldTransform(store, manipId);
  if (!nodeTransform) return;

  const cameraWorldMatrix = getCameraWorldTransform(store);
  nodeTransform.decompose(nodePosition, nodeRotationQuaternion, nodeScale);
  cameraPosition.setFromMatrixPosition(cameraWorldMatrix);

  const radius = getBoundingRadius(store, manipId);

  let relativeDelta = new THREE.Vector3().set(2 * x / rect.x / radius, -2 * y / rect.y / radius, 0);

  if (relativeDelta.length() > 1) relativeDelta.normalize();
  else if (relativeDelta.length() === 0) return;

  let delta = relativeDelta.length();
  relativeDelta.transformDirection(cameraWorldMatrix).multiplyScalar(delta);

  const objNormal = new THREE.Vector3().subVectors(cameraPosition, nodePosition).normalize();

  if (restrictToAxis) {
    const projVector = new THREE.Vector3(0, 0, 0);
    projVector[restrictToAxis] = 1;
    relativeDelta.projectOnPlane(projVector);
    objNormal.projectOnPlane(projVector);
  }

  const scaleFactor = sqrt(1 - relativeDelta.lengthSq());
  relativeDelta.addScaledVector(objNormal, scaleFactor).normalize();
  const modifyQuaternion = new THREE.Quaternion().setFromUnitVectors(objNormal, relativeDelta);
  nodeRotationQuaternion.premultiply(modifyQuaternion);

  nodeTransform.compose(nodePosition, nodeRotationQuaternion, nodeScale);
  const parentMatrix = getWorldTransform(store, find(store, { id: manipId, parent: true }));
  if (parentMatrix) {
    parentInvMatrix.getInverse(parentMatrix, true);
    nodeTransform.premultiply(parentInvMatrix);
  }

  nodeTransform.decompose(nodePosition, nodeRotationQuaternion, nodeScale);

  // FIXME: resrictToAxis
  // console.log('restrictToAxis: ', restrictToAxis);

  nodeRotationEuler.setFromQuaternion(nodeRotationQuaternion, 'ZYX');
  store.dispatch(set({ id: manipId, plug: 'Transform', property: 'rotation' }, nodeRotationEuler.toVector3().multiplyScalar(180 / PI)));
}



// WEBPACK FOOTER //
// ./src/translators/tools/nodeRotate.js